import type { AnalyticsSender, Offer } from '@funtech-int/yango-shared';

import {
    HeaderNavigatedTo,
    headerNavigated,
    headerShowed,
    headerSubscriptionOfferNavigated,
    headerSubscriptionOfferShowed,
    headerSubscriptionOfferSucceed,
} from '@kinopoisk-int/evgen-web-events/web/header';
import { HeaderOfferEntityTypes } from '@kinopoisk-int/evgen-web-events/web/named_enums';

import { TARIFFICATOR_BILLING_PRODUCT_ID } from '@/shared/lib/analytics/offer';
import { PageId } from '@/shared/types/pathId';
import { getBillingProductIds, getOfferPositionsData, getOfferTitle } from '@/shared/lib/analytics';

const mapPageIdToHeaderNavigatedTo: Partial<Record<string, HeaderNavigatedTo>> = {
    [PageId.MAIN]: HeaderNavigatedTo.MyMoviesScreen,
    [PageId.CHANNELS]: HeaderNavigatedTo.TvchannelsScreen,
    [PageId.PERSONAL]: HeaderNavigatedTo.MypageScreen,
    [PageId.LOGIN]: HeaderNavigatedTo.AuthScreen,
};

const transformPageIdToHeaderNavigatedTo = ({
    hasSubscription,
    pageId,
}: {
    hasSubscription: boolean;
    pageId?: string;
}): HeaderNavigatedTo | undefined => {
    let result = pageId ? mapPageIdToHeaderNavigatedTo[pageId] : undefined;
    if (!result) {
        return undefined;
    }
    if (!hasSubscription && result === HeaderNavigatedTo.MyMoviesScreen) {
        result = HeaderNavigatedTo.OnboardingPlusScreen;
    }

    return result;
};

export const createAnalyticsEvents = (eventSender: AnalyticsSender) => ({
    headerShowed() {
        eventSender.sendOnce(headerShowed, {});
    },
    headerNavigated(pageId: string | undefined, hasSubscription: boolean) {
        const to = pageId
            ? transformPageIdToHeaderNavigatedTo({
                  pageId,
                  hasSubscription,
              })
            : undefined;

        if (to) {
            eventSender.send(headerNavigated, { to });
        }
    },
    createOfferAnalyticsEvents(offer: Offer | null | undefined, buttonText: string) {
        const { tariffOffer, optionOffers = [] } = offer?.catalogCompositeOffer || {};
        const { batchId = '', positionId = '' } = offer?.analyticData || {};
        const tariffName = tariffOffer?.name;
        const optionName = optionOffers[0]?.name;

        const offerPositionsData = getOfferPositionsData({
            offersBatchId: batchId,
            offersPositionId: positionId,
        });

        const eventOfferMeta = {
            entityType: HeaderOfferEntityTypes.SubscriptionOption,
            offerTitle: getOfferTitle(offer),
            buttonText,
            billingProductId: TARIFFICATOR_BILLING_PRODUCT_ID,
            billingProductIds: getBillingProductIds({
                tariffOfferName: tariffName,
                optionOfferNames: optionName,
            }),
            offerTariffName: tariffName ?? '',
            offerOptionNames: optionName ?? '',
        };

        return {
            showed() {
                eventSender.send(headerSubscriptionOfferShowed, {
                    ...eventOfferMeta,
                    show: offerPositionsData,
                });
            },
            navigated() {
                eventSender.send(headerSubscriptionOfferNavigated, {
                    ...eventOfferMeta,
                    click: offerPositionsData,
                });
            },
            succeeded() {
                eventSender.send(headerSubscriptionOfferSucceed, {
                    ...eventOfferMeta,
                });
            },
        };
    },
});
